/**
 * settingsStore.ts — the ONE owner of the settings-blob read-modify-write.
 *
 * `settings` is a single global KV blob and every write replaces the whole
 * blob (KV is last-write-wins). updateSettings serializes concurrent same-tab
 * writers on a promise chain so the second save can't drop the first's field;
 * cross-tab/cross-device writers are NOT solved client-side and still race.
 *
 * Skip-save convention: if `mutate` returns its input UNCHANGED (same object
 * reference), the save is skipped. Returning a new object always saves.
 */
import { getSettings, saveSettings, type Settings } from './portfolioApi';

let tail: Promise<unknown> = Promise.resolve();

/**
 * Read-modify-write the settings blob, serialized per tab. Returns the blob
 * now persisted. Errors from the fetch, mutator, or save propagate to THIS
 * call's caller and never block later queued updates.
 */
export function updateSettings(mutate: (s: Settings) => Settings): Promise<Settings> {
  const run = tail.then(async () => {
    const fresh = await getSettings();
    const next = mutate(fresh);
    if (next === fresh) return fresh;
    await saveSettings(next);
    return next;
  });
  tail = run.then(() => undefined, () => undefined);
  return run;
}
